import React, { useState, useEffect } from "react";
import styles from "./performanceIndicator.css";
import classNames from "classnames";
import { TooltipComponent } from "@fidget/common/components/tooltipComponent";

export interface PerformanceData {
  value: number;
  formattedValue?: string;
  ranges?: Ranges;
}

export interface Ranges {
  belowBound: number;
  aboveBound: number;
}

interface Props {
  iconClassName: string;
  tooltipText: JSX.Element;
  autoHideTooltip?: boolean;
  suffix?: string;
  data: PerformanceData;
  decimal?: boolean;
  oneColor?: boolean;
  maxWidth?: string;
  minWidth?: string;
}

const getColor = (value: number, ranges?: Ranges, oneColor?: boolean): string => {
  if (oneColor || ranges === undefined) {
    return styles.oneColor;
  }

  if (ranges && value <= ranges.belowBound) {
    return styles.good;
  }

  if (ranges && value >= ranges.aboveBound) {
    return styles.bad;
  }

  return styles.warn;
};

export const PerformanceIndicator = ({
  iconClassName,
  data,
  tooltipText,
  autoHideTooltip,
  suffix,
  oneColor,
  decimal,
  maxWidth,
  minWidth,
}: Props) => {
  autoHideTooltip ??= true;

  const [color, setColor] = useState<string>(getColor(data.value, data.ranges, oneColor));
  const displayedNumber = data.formattedValue ?? (decimal ? data.value.toFixed(2) : data.value);

  useEffect(() => {
    if (!oneColor) {
      setColor(getColor(data.value, data.ranges));
    }
  }, [data]);

  return (
    <TooltipComponent
      text={tooltipText}
      positionPreset={"bottom"}
      style={"dashboard"}
      maxWidth={maxWidth}
      minWidth={minWidth}
      autoHide={autoHideTooltip}
    >
      <div className={classNames(styles.indicator, color)}>
        <div>
          {displayedNumber + (suffix !== undefined ? " " + suffix : "")}
          <i className={iconClassName} />
        </div>
        <div className={styles.underline} />
      </div>
    </TooltipComponent>
  );
};

interface MockedProps {
  iconClassName: string;
  suffix?: string;
  data: string;
}

export const MockedPerfomanceIndicator = ({ iconClassName, data, suffix }: MockedProps) => {
  return (
    <div className={classNames(styles.indicator, styles.oneColorBlack)}>
      <div>
        {data + (suffix !== undefined ? " " + suffix : "")}
        <i className={iconClassName} />
      </div>
      <div className={styles.underline} />
    </div>
  );
};
